import { useCallback, useMemo, useRef } from 'react';
import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { addDays, format } from 'date-fns';
import { getAppointmentClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import type { DaySlotsQuery, DaySlotsResponse } from '@vagusx/clients';

export interface DaySlotsFilters {
  date: Date;
  providerIds?: string[];
  locationIds?: string[];
}

function daySlotsQuery(filters: DaySlotsFilters): DaySlotsQuery {
  return {
    date: format(filters.date, 'yyyy-MM-dd'),
    providerIds: filters.providerIds,
    locationIds: filters.locationIds,
  };
}

/**
 * Hook to fetch day slots for timeline view.
 */
export function useDaySlots(filters: DaySlotsFilters) {
  const query = daySlotsQuery(filters);

  return useQuery({
    queryKey: queryKeys.appointments.daySlots(query),
    queryFn: () => getAppointmentClient().getDaySlots(query),
  });
}

export interface DaySlotsRangeFilters {
  startDate: Date;
  /** How many consecutive days, starting at `startDate`. */
  days: number;
  providerIds?: string[];
  locationIds?: string[];
}

interface DaySlotsRangeResult {
  /** Each loaded day in order; a day still loading or that failed is left out. */
  days: Array<{ date: Date; data: DaySlotsResponse }>;
  isLoading: boolean;
}

/**
 * Day slots for several consecutive days — one day-slots request per day, under
 * the same cache key {@link useDaySlots} uses, so a day already seen in either
 * view is not fetched again.
 */
export function useDaySlotsRange(filters: DaySlotsRangeFilters): DaySlotsRangeResult {
  const { startDate, days, providerIds, locationIds } = filters;
  const dates = useMemo(
    () => Array.from({ length: days }, (_, i) => addDays(startDate, i)),
    [startDate, days]
  );

  // `combine` re-runs on any result change, a background refetch's isFetching
  // flip included. The grid keys scroll and layout work off `days`, so it keeps
  // the previous array until a day's data (structurally shared) actually changes.
  const lastDays = useRef<{
    dates: Date[];
    data: unknown[];
    days: DaySlotsRangeResult['days'];
  } | null>(null);
  const combine = useCallback(
    (results: Array<UseQueryResult<DaySlotsResponse>>): DaySlotsRangeResult => {
      const data = results.map((r) => r.data);
      const last = lastDays.current;
      const unchanged =
        last?.dates === dates &&
        last.data.length === data.length &&
        last.data.every((d, i) => d === data[i]);
      const days =
        unchanged && last
          ? last.days
          : dates.flatMap((date, i) => {
              const day = data[i];
              return day ? [{ date, data: day }] : [];
            });
      lastDays.current = { dates, data, days };
      return { days, isLoading: results.some((r) => r.isLoading) };
    },
    [dates]
  );

  return useQueries({
    queries: dates.map((date) => {
      const query = daySlotsQuery({ date, providerIds, locationIds });
      return {
        queryKey: queryKeys.appointments.daySlots(query),
        queryFn: () => getAppointmentClient().getDaySlots(query),
      };
    }),
    combine,
  });
}
